import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useReactTable,
  getCoreRowModel,
  getExpandedRowModel,
  createColumnHelper,
  flexRender,
} from "@tanstack/react-table";
import StatusBadge from "../../generals/StatusBadge";

const columnHelper = createColumnHelper();

const buildHierarchy = (items) => {
  const map = {};
  const roots = [];

  items.forEach((item) => {
    map[item.Account_Id] = { ...item, key: item.Account_Id, children: [] };
  });

  items.forEach((item) => {
    if (item.Parent_Account_Id && map[item.Parent_Account_Id]) {
      map[item.Parent_Account_Id].children.push(map[item.Account_Id]);
    } else {
      roots.push(map[item.Account_Id]);
    }
  });

  return roots;
};

// Walk the ancestor chain and return a connector type per indentation column:
//   "line"  -> vertical rail passing through (ancestor has more siblings below)
//   "blank" -> empty column (ancestor branch already ended)
//   "mid"   -> elbow for a middle child (├)
//   "end"   -> elbow for the last child (└)
const getRowConnectors = (row) => {
  const path = [];
  let cur = row;
  while (cur) {
    path.unshift(cur);
    cur = cur.getParentRow?.();
  }
  const connectors = [];
  for (let k = 0; k < path.length - 1; k++) {
    const parent = path[k];
    const node = path[k + 1];
    const siblings = parent.subRows || [];
    const isLast = siblings[siblings.length - 1]?.id === node.id;
    if (k === path.length - 2) {
      connectors.push(isLast ? "end" : "mid");
    } else {
      connectors.push(isLast ? "blank" : "line");
    }
  }
  return connectors;
};

const TreeConnector = ({ type }) => (
  <span className="relative w-5 shrink-0 self-stretch">
    {(type === "line" || type === "mid") && (
      <span className="absolute bottom-0 top-0 left-1/2 w-[1.5px] -translate-x-1/2 bg-gray-400" />
    )}
    {type === "end" && (
      <span className="absolute top-0 left-1/2 h-1/2 w-[1.5px] -translate-x-1/2 bg-gray-400" />
    )}
    {(type === "mid" || type === "end") && (
      <span className="absolute left-1/2 right-0 top-1/2 h-[1.5px] -translate-y-1/2 bg-gray-400" />
    )}
  </span>
);

const formatBalance = (amount, currencyShort) => {
  const value = Number(amount) || 0;
  const formatted = new Intl.NumberFormat("en-NG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(value));
  const sign = value < 0 ? "-" : "";
  const prefix = currencyShort === "NGN" ? "₦" : `${currencyShort || ""} `;
  return `${sign}${prefix}${formatted}`;
};

const ChartOfAccountTable = ({ tableData, selectedTableItems }) => {
  const [treeData, setTreeData] = useState([]);
  const [expanded, setExpanded] = useState(true);
  const [rowSelection, setRowSelection] = useState({});
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const url = pathname.split("/");
  const currModuleId = url[1];
  const currModuleName = url[2];
  const currUserMenu = url[3];

  useEffect(() => {
    if (tableData?.length) {
      setTreeData(buildHierarchy(tableData));
    } else {
      setTreeData([]);
    }
    setRowSelection({});
  }, [tableData]);

  const goToDetails = (rowData) => {
    navigate(
      `/${currModuleId}/${currModuleName}/${currUserMenu}/details/${rowData?.Account_Id}`
    );
  };

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "select",
        size: 44,
        header: ({ table }) => (
          <input
            type="checkbox"
            className="h-4 w-4 cursor-pointer accent-blue-600"
            checked={table.getIsAllRowsSelected()}
            ref={(el) => {
              if (el) el.indeterminate = table.getIsSomeRowsSelected();
            }}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            className="h-4 w-4 cursor-pointer accent-blue-600"
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            onChange={row.getToggleSelectedHandler()}
            onClick={(e) => e.stopPropagation()}
          />
        ),
      }),
      columnHelper.accessor("Account_Name", {
        header: "Account Name",
        meta: { tdClassName: "relative px-4 py-0 align-middle" },
        cell: ({ row, getValue }) => {
          const connectors = getRowConnectors(row);
          return (
            <>
              <div className="pointer-events-none absolute inset-y-0 left-4 flex">
                {connectors.map((type, i) => (
                  <TreeConnector key={i} type={type} />
                ))}
              </div>
              <div
                className="relative flex min-h-[44px] items-center"
                style={{ paddingLeft: `${connectors.length * 20}px` }}
              >
                {row.getCanExpand() ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    row.toggleExpanded();
                  }}
                  className="mr-1.5 flex h-5 w-5 shrink-0 items-center justify-center rounded text-gray-500 hover:bg-gray-200"
                  aria-label={row.getIsExpanded() ? "Collapse" : "Expand"}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    className={`h-3.5 w-3.5 transition-transform ${
                      row.getIsExpanded() ? "rotate-90" : ""
                    }`}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              ) : (
                <span className="mr-1.5 inline-block w-5 shrink-0" />
              )}
                <span className="font-medium text-blue-600 hover:underline">
                  {getValue()}
                </span>
              </div>
            </>
          );
        },
      }),
      columnHelper.accessor("Account_Number", {
        header: "Account Code",
        cell: ({ getValue }) => (
          <span className="text-gray-600">{getValue() || "—"}</span>
        ),
      }),
      columnHelper.accessor("Account_Type_Name", {
        header: "Account Type",
        cell: ({ getValue }) => (
          <span className="text-gray-700">{(getValue() || "").trim()}</span>
        ),
      }),
      columnHelper.accessor("Parent_Account_Name", {
        header: "Parent Account",
        cell: ({ getValue }) => (
          <span className="text-gray-600">{getValue() || "—"}</span>
        ),
      }),
      columnHelper.accessor("Account_Head_Opening_Amount", {
        header: () => <div className="text-right">Closing Balance</div>,
        cell: ({ row, getValue }) => (
          <div className="text-right font-medium text-gray-800 tabular-nums">
            {formatBalance(getValue(), row.original?.Currency_Name_Short)}
          </div>
        ),
      }),
      columnHelper.accessor("Account_Status_Name", {
        header: "Status",
        cell: ({ row, getValue }) => (
          <StatusBadge
            statusId={row.original?.Account_Status_Id}
            statusName={getValue()}
          />
        ),
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const table = useReactTable({
    data: treeData,
    columns,
    state: { expanded, rowSelection },
    getRowId: (row) => String(row.Account_Id),
    onExpandedChange: setExpanded,
    onRowSelectionChange: setRowSelection,
    getSubRows: (row) => row.children,
    enableRowSelection: true,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });

  useEffect(() => {
    const ids = table
      .getSelectedRowModel()
      .rows.map((r) => r.original?.Account_Id);
    selectedTableItems?.(ids);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowSelection]);

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow">
      <div className="max-h-[600px] overflow-auto">
        <table className="w-full border-collapse text-sm">
          <thead className="sticky top-0 z-10 bg-gray-50">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="border-b border-gray-200">
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    style={{ width: header.getSize() }}
                    className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-gray-100">
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                onClick={() => goToDetails(row.original)}
                className={`cursor-pointer transition-colors hover:bg-gray-50 ${
                  row.getIsSelected() ? "bg-blue-50" : ""
                }`}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={
                      cell.column.columnDef.meta?.tdClassName ??
                      "px-4 py-3 align-middle"
                    }
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
            {table.getRowModel().rows.length === 0 && (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-10 text-center text-gray-400"
                >
                  No accounts to display
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ChartOfAccountTable;
